import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { AxiosError } from "axios";
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { getUserOrgId } from "../getUserOrgId";
import _deleteSite from "../http/deleteSite";
import { applySiteDeletedCacheUpdates } from "./deleteCacheUpdates";
import deleteSiteMarketplaceMatch from "../http/deleteSiteMarketplaceMatch";
import getSite from "../http/getSite";
import getSiteLog from "../http/getSiteLog";
import getSiteReport from "../http/getSiteReport";
import getSiteValues from "../http/getSiteValues";
import postPreviewMultisiteSpv from "../http/postPreviewMultisiteSpv";
import postPreviewSite from "../http/postPreviewSite";
import postSite from "../http/postSite";
import postSiteCopy from "../http/postSiteCopy";
import postSiteLogPreview from "../http/postSiteLogPreview";
import postSiteMarketplaceMatch from "../http/postSiteMarketplaceMatch";
import postSiteReportPreview from "../http/postSiteReportPreview";
import postSitesReport from "../http/postSitesReport";
import putSite from "../http/putSite";
import {
  AllowStringForNumbers,
  CostInputsInrange,
  Site,
  SiteChangeLog,
  SitePreviewError,
  SiteValuesOptions,
} from "../models/site";
import { ReloginContext } from "../ReloginContext";
import { getAuthTokenBlocking } from "./utils";

export interface UseSiteProps {
  siteId?: string;
  userOrgId?: string;
  enableNonEssentialQueries?: boolean;
  app: "admin" | "customer";
}

const useSite = ({
  siteId,
  userOrgId,
  enableNonEssentialQueries,
  app,
}: UseSiteProps) => {
  if (enableNonEssentialQueries === undefined) {
    // This enables useSite to be mounted multiple times without
    // triggering automatic fetches of the site log and the site default values
    enableNonEssentialQueries = true;
  }
  const isAdmin = app === "admin";
  const userOrgIdArg = userOrgId;

  const queryClient = useQueryClient();
  const { getToken, getClaim } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);

  const params = new URLSearchParams(window.location.search);
  const testOrgId = params.get("testOrgId");

  const fetchSiteValues: UseQueryResult<
    {
      site: Site;
      options: SiteValuesOptions;
    },
    unknown
  > = useQuery(
    ["site-values"],
    async () => {
      return getSiteValues(await getAuthTokenBlocking(getToken, relogin));
    },
    {
      // Allow query to be mounted multiple times on the same page without re-fetching
      staleTime: 5 * 60 * 1000,
      enabled: enableNonEssentialQueries && isAdmin,
    }
  );

  const LOSS_FACTORS_BY_KEY: Record<
    string,
    {
      label: string;
      key: string;
      deprecated?: boolean;
    }
  > = {};
  ((fetchSiteValues.data || {})?.options?.pvLossFactorLabels || []).forEach(
    (lf: any, index: number) => {
      if (LOSS_FACTORS_BY_KEY[lf.key]) {
        throw new Error(`Duplicate loss factor key: ${lf.key}`);
      }
      LOSS_FACTORS_BY_KEY[lf.key] = { ...lf, index };
    }
  );

  const fetchSite: UseQueryResult<{ site: Site }, unknown> = useQuery(
    ["site", siteId],
    async () => {
      return getSite(
        await getAuthTokenBlocking(getToken, relogin),
        siteId!,
        await getUserOrgId(getClaim, userOrgId),
        testOrgId
      );
    },
    {
      enabled: !!siteId,
      // Allow query to be mounted multiple times on the same page without re-fetching
      staleTime: 5 * 60 * 1000,
    }
  );

  const fetchSiteLog: UseQueryResult<{ siteLog: SiteChangeLog[] }, unknown> =
    useQuery(
      ["site", siteId, "log"],
      async () => {
        return getSiteLog(
          await getAuthTokenBlocking(getToken, relogin),
          siteId!
        );
      },
      {
        enabled: !!siteId && isAdmin && enableNonEssentialQueries,
        // Allow query to be mounted multiple times on the same page without re-fetching
        staleTime: 5 * 60 * 1000,
      }
    );

  const fetchSiteReport = useMutation(
    async ({
      siteName,
      reportName,
      options,
      fileName,
      esysCount,
    }: {
      siteName: string;
      reportName: string;
      options?: object;
      fileName: string;
      esysCount?: number;
    }) =>
      getSiteReport(
        await getAuthTokenBlocking(getToken, relogin),
        siteId!,
        siteName,
        reportName,
        options,
        fileName,
        esysCount,
        await getUserOrgId(getClaim, userOrgId)
      )
  );

  const fetchSiteReportPreview = useMutation(
    async ({
      payload,
      siteName,
      reportName,
      options,
      fileName,
    }: {
      payload: any;
      siteName: string;
      reportName: string;
      options?: object;
      fileName: string;
    }) =>
      postSiteReportPreview(
        await getAuthTokenBlocking(getToken, relogin),
        payload,
        siteName,
        reportName,
        options,
        fileName
      )
  );

  const fetchSitesReport = useMutation(
    async ({
      siteIds,
      payload,
      prefixName,
      reportName,
      options,
      fileName,
    }: {
      siteIds: string[];
      payload?: any;
      prefixName: string;
      reportName: string;
      options?: object;
      fileName: string;
    }) =>
      postSitesReport(
        await getAuthTokenBlocking(getToken, relogin),
        siteIds,
        payload,
        prefixName,
        reportName,
        options,
        fileName
      )
  );

  const updateSite = useMutation(
    async (update: any) =>
      putSite(
        await getAuthTokenBlocking(getToken, relogin),
        siteId!,
        await getUserOrgId(getClaim, userOrgId),
        update
      ),
    {
      onSuccess: (_) => {
        queryClient.invalidateQueries(["sites"]);
        queryClient.invalidateQueries(["site", siteId]);
        if (!isAdmin) {
          queryClient.invalidateQueries(["org", userOrgIdArg]);
        }
      },
    }
  );

  const createSite = useMutation(
    async (site: any) =>
      postSite(
        await getAuthTokenBlocking(getToken, relogin),
        site,
        await getUserOrgId(getClaim, userOrgId)
      ),
    {
      onSuccess: () => {
        if (!isAdmin) {
          queryClient.invalidateQueries(["org", userOrgIdArg]);
        }
      },
    }
  );

  const copySite = useMutation(
    async ({ siteId, copyBody }: { siteId: string; copyBody: any }) =>
      postSiteCopy(
        await getAuthTokenBlocking(getToken, relogin),
        siteId,
        copyBody
      )
  );

  const previewSite = useMutation<
    { site: Site },
    AxiosError<SitePreviewError>,
    unknown
  >(
    async (payload: any) =>
      postPreviewSite(await getAuthTokenBlocking(getToken, relogin), payload),
    {
      // Cold starts can make the first couple of runs take longer than the API gateway 30 second
      // timeout so we allow two retries
      retry: (failureCount: number, error: AxiosError) => {
        // Disable retry if the error status is 400
        if (error.response?.status === 400) {
          return false;
        }
        // Allow retry for other errors, up to 2 times
        return failureCount < 2;
      },
    }
  );

  const previewMultisiteSpv = useMutation<
    {
      siteIds: string[];
      costInputsInrange: Partial<AllowStringForNumbers<CostInputsInrange>>;
    },
    AxiosError<SitePreviewError>,
    unknown
  >(async (payload: any) =>
    postPreviewMultisiteSpv(
      await getAuthTokenBlocking(getToken, relogin),
      payload
    )
  );

  const deleteSite = useMutation(
    async (siteId: string) =>
      _deleteSite(await getAuthTokenBlocking(getToken, relogin), siteId),
    {
      onSuccess: (_data, deletedSiteId) =>
        applySiteDeletedCacheUpdates(queryClient, deletedSiteId),
    }
  );

  const siteAddMatch = useMutation(
    async (match: any) => {
      return await postSiteMarketplaceMatch(
        await getAuthTokenBlocking(getToken, relogin),
        siteId!,
        match
      );
    },
    {
      onSettled: (_data, _error, match) => {
        queryClient.invalidateQueries(["sites"]);
        queryClient.invalidateQueries(["site", match.buyerId]);
        queryClient.invalidateQueries(["site", match.sellerId]);
        // Only done from Admin, so no need to invalidate the Customer App only org query which returns sites
      },
    }
  );

  const siteDeleteMatch = useMutation(
    async (match: any) => {
      return await deleteSiteMarketplaceMatch(
        await getAuthTokenBlocking(getToken, relogin),
        siteId!,
        match
      );
    },
    {
      onSettled: (_data, _error, match) => {
        queryClient.invalidateQueries(["sites"]);
        queryClient.invalidateQueries(["site", match.buyerId]);
        queryClient.invalidateQueries(["site", match.sellerId]);
        // Only done from Admin, so no need to invalidate the Customer App only org query which returns sites
      },
    }
  );

  const previewSiteLog = useMutation(async (update: any) =>
    postSiteLogPreview(
      await getAuthTokenBlocking(getToken, relogin),
      siteId!,
      update
    )
  );

  return {
    fetchSiteValues,
    LOSS_FACTORS_BY_KEY,
    fetchSite,
    fetchSiteLog,
    fetchSiteReport,
    fetchSiteReportPreview,
    fetchSitesReport,
    createSite,
    previewSite,
    updateSite,
    deleteSite,
    copySite,
    siteAddMatch,
    previewMultisiteSpv,
    siteDeleteMatch,
    previewSiteLog,
  };
};

export default useSite;
